import { CancelButton } from '@/components/ImportExportHistory/Table/Columns/Cells/components/CancelButton';
import { DetailsButton } from '@/components/ImportExportHistory/Table/Columns/Cells/components/DetailsButton';
import { DownloadButton } from '@/components/ImportExportHistory/Table/Columns/Cells/components/DownloadButton';
import { useStatusLabel } from '@/components/ImportExportHistory/Table/hooks/useStatusLabel';
import {
  ImportExportStatus,
  ImportExportType,
  OnDownloadClick,
} from '@/components/ImportExportHistory/type';
import { useTranslation } from '@/i18n';
import {
  Caption,
  IconApproval,
  IconCancel,
  IconError,
  IconPending,
} from '@moneyforward/ap-frontend-components';
import cx from 'classnames';
import { FC, memo, useCallback } from 'react';
import styles from '../column.module.scss';

type Props = {
  status: ImportExportStatus;
  canCancel?: boolean;
  onCancelClick?: () => Promise<void>;
  onDetailsClick?: () => void;
  type?: ImportExportType;
  downloadFileId?: string;
  canDownload?: boolean;
  onDownloadClick?: OnDownloadClick;
  errorMessage?: string | null;
};

const CAPTION_TYPE_MAP = {
  processing: 'success',
  failed: 'error',
  done: 'helper',
  canceled: 'helper',
} as const;

const getCaptionType = (status: ImportExportStatus) => CAPTION_TYPE_MAP[status];

const getIconComponent = (status: ImportExportStatus) => {
  switch (status) {
    case 'processing':
      return IconPending;
    case 'failed':
      return IconError;
    case 'done':
      return IconApproval;
    case 'canceled':
      return IconCancel;
    default:
      return null;
  }
};

export const StatusCell: FC<Props> = memo(
  ({
    status,
    canCancel,
    onCancelClick,
    onDetailsClick,
    type,
    downloadFileId,
    canDownload,
    onDownloadClick,
    errorMessage,
  }) => {
    const { t } = useTranslation();
    const label = useStatusLabel(status);
    const captionType = getCaptionType(status);
    const IconComponent = getIconComponent(status);
    const isDefaultStyle = captionType === 'helper';

    const showExportDownload = type === 'export' && status === 'done';

    const handleDownloadClick = useCallback(async () => {
      if (downloadFileId !== undefined && onDownloadClick) {
        await onDownloadClick(downloadFileId);
      }
    }, [downloadFileId, onDownloadClick]);

    const showDetailsButton =
      type === 'import' &&
      (status === 'failed' || status === 'canceled') &&
      onDetailsClick;
    const showErrorMessage =
      (status === 'failed' || status === 'canceled') && !!errorMessage;
    const showCancelButton =
      status === 'processing' &&
      type === 'import' &&
      canCancel &&
      onCancelClick;

    const iconWrapperClass = isDefaultStyle
      ? cx(styles['status-default-icon'])
      : undefined;
    const labelClass = cx(
      styles['status-label'],
      isDefaultStyle && styles['status-default-text']
    );

    return (
      <div className={styles['status-cell-wrapper']}>
        <div className={styles['status-cell']}>
          <div className={styles['status-label-area']}>
            <Caption
              type={captionType}
              icon={false}
              data-testid={`caption-${captionType}`}
            >
              <div className={styles['status-cell-content']}>
                {IconComponent && (
                  <div className={iconWrapperClass}>
                    <IconComponent width={16} height={16} />
                  </div>
                )}
                <span className={labelClass}>{label}</span>
              </div>
            </Caption>
          </div>
          {showExportDownload && (
            <>
              <DownloadButton
                onClick={handleDownloadClick}
                isDisabled={!canDownload}
                tooltipTitle={
                  !canDownload
                    ? t('import_export_history_download_expired')
                    : undefined
                }
                variant='text'
              />
            </>
          )}
          {showCancelButton && <CancelButton onClick={onCancelClick} />}
          {showDetailsButton && <DetailsButton onClick={onDetailsClick} />}
        </div>
        {showErrorMessage && (
          <div className={styles['status-error-message']}>{errorMessage}</div>
        )}
      </div>
    );
  }
);

StatusCell.displayName = 'StatusCell';
